/**
 * LINTICO UK — Size Chart modal behaviour (Task T5).
 *
 * Contract : docs/size-chart-integration.md
 *            §2.2 — modal DOM skeleton + data attributes (snippets/product-popups.liquid)
 *            §2.3 — fetch trigger, request shape, render order, state machine.
 * Styles   : assets/size-chart.css (Task T4) — .szc-* class inventory,
 *            row highlight class is `.szc-table .tr--highlight`.
 * Runtime  : IIFE + 'use strict', zero dependencies, defer-friendly (also runs
 *            after DOMContentLoaded). Each modal open revalidates its data;
 *            concurrent requests are deduplicated. Unit switching re-renders locally; variant changes revalidate the selected chart.
 *
 * State machine (status container [data-size-chart-status], aria-live=polite):
 *   loading     → spinner + neutral text
 *   ready       → status hidden, data area rendered
 *   unavailable → 404 UNSUPPORTED_PRODUCT / SIZE_CHART_MISSING: guidance copy
 *                 + "Find your perfect fit" CTA (opens smart-size modal) + retry
 *   error       → network / timeout / non-404 HTTP / bad JSON: error copy + retry
 */
(() => {
  'use strict';

  const REQUEST_TIMEOUT_MS = 8000;
  const CM_PER_IN = 2.54;
  /* Contract §3 has no loading key; this is the only hardcoded user-visible
   * string, allowed by the T5 task spec. */
  const LOADING_TEXT = 'Loading size chart…';

  /* English fallbacks — used only when the Liquid labels JSON is missing a key. */
  const FALLBACK_LABELS = {
    measurements_tab: 'Product Measurements',
    fit_guide_tab: 'Fit Guide',
    table_header_size: 'Size',
    model_ref_title: 'Model Reference',
    model_ref_subtitle: 'Body measurements and try-on size',
    model_height: 'Height',
    model_bust: 'Bust',
    model_waist: 'Waist',
    model_hips: 'Hips',
    model_tryon_size: 'Try-on size',
    fit_type_label: 'Fit',
    material_label: 'Material',
    stretch_label: 'Stretch',
    design_label: 'Design details',
    notes_label: 'Notes',
    how_to_measure: 'How to measure',
    how_to_measure_body: '',
    map_title: 'Measurement Map',
    product_details: 'Product details',
    customer_reviews: 'Customer reviews',
    read_reviews: 'Read reviews',
    review_count: 'reviews',
    review_count_one: 'review',
    no_reviews: 'No reviews yet.',
    reviews_unavailable: 'Reviews are available on the product page.',
    product_model: 'Product model',
    model_ref_notice: 'Body measurements are not available for this item yet.',
    customer_fit_photos: 'Customer Fit Photos',
    customer_fit_photo: 'Customer photo',
    customer_fit_empty: 'Customer fit photos will appear here when available.',
    product_photo: 'Product photo',
    product_photo_note: 'Current product media · fit reference',
    unavailable_contact_message: 'The size chart for this item is currently unavailable. Please contact us for sizing assistance.',
    retry: 'Try again',
    error_loading: 'Something went wrong loading the size chart.',
    how_to_measure_title: "How to Measure Your Body",
    measure_shoulder_label: "Shoulder",
    measure_shoulder_copy: "Measure across your back from shoulder tip to shoulder tip.",
    measure_bust_label: "Bust",
    measure_bust_copy: "Measure around the fullest part of your bust.",
    measure_waist_label: "Waist",
    measure_waist_copy: "Measure around the narrowest part of your natural waist.",
    measure_hips_label: "Hips",
    measure_hips_copy: "Measure around the fullest part of your hips.",
    sizing_help_title: "Need personalized help with sizing?",
    sizing_help_contact: "Contact us",
    customer_wears: "Wears",
    smart_size_cta: 'Find your perfect fit'
  };

  /* Contract §1.2 rule 5: fit_type passes through in Chinese; EN mapping lives here. */
  const FIT_TYPE_EN = {
    常规: 'Regular',
    修身: 'Slim',
    宽松: 'Relaxed',
    超宽松: 'Oversized',
    紧身: 'Tight'
  };

  // English display vocabulary for the current 366-record size catalog.
  // This is presentation only; source records and recommendation rules stay unchanged.
  const FIT_GUIDE_EN = {
    "亚麻": "Linen",
    "亚麻-莱赛尔混纺": "Linen–lyocell blend",
    "亚麻-锦纶混纺": "Linen–nylon blend",
    "亚麻棉混纺": "Linen–cotton blend",
    "亚麻混纺": "Linen blend",
    "人棉": "Rayon",
    "人棉混纺": "Rayon blend",
    "双层皱布": "Double-layer crinkle fabric",
    "平纹针织": "Jersey knit",
    "棉": "Cotton",
    "棉-氨纶混纺": "Cotton–elastane blend",
    "氨纶": "Elastane",
    "涤纶": "Polyester",
    "真丝": "Silk",
    "粘胶": "Viscose",
    "羊毛": "Wool",
    "羊毛混纺": "Wool blend",
    "羊绒": "Cashmere",
    "美丽诺羊毛": "Merino wool",
    "聚酯纤维": "Polyester",
    "腈纶": "Acrylic",
    "苎麻-莱赛尔混纺": "Ramie–lyocell blend",
    "莱赛尔": "Lyocell",
    "醋酸": "Acetate",
    "锦纶": "Nylon",
    "无弹": "Non-stretch",
    "有弹性": "Stretch",
    "一字口袋": "Welt pockets",
    "不对称": "Asymmetric",
    "不规则下摆": "Asymmetric hem",
    "中式元素": "Chinese-inspired details",
    "串珠装饰": "Beaded details",
    "侧摆开衩": "Side hem slits",
    "侧缝前移": "Forward side seams",
    "侧缝口袋": "Side-seam pockets",
    "侧缝开衩": "Side-seam slits",
    "侧缝开门襟": "Side-seam placket",
    "倒褶": "Inverted pleats",
    "假门襟": "Faux placket",
    "全松紧腰": "Fully elasticated waist",
    "内工字褶": "Inverted box pleats",
    "刺绣": "Embroidery",
    "刺绣织带": "Embroidered trim",
    "前襟开衩": "Front placket slit",
    "前高后低下摆": "High-low hem",
    "半开门襟": "Half placket",
    "半松紧腰": "Partially elasticated waist",
    "单唇袋": "Single-welt pockets",
    "印花": "Print",
    "压褶": "Pleats",
    "双唇袋": "Double-welt pockets",
    "双排扣": "Double-breasted buttons",
    "双面可穿": "Reversible",
    "后领开衩": "Back neckline slit",
    "垂褶": "Draping",
    "塔克条": "Pintucks",
    "复合褶": "Combination pleats",
    "对丝": "Ladder-stitch detail",
    "工字褶": "Box pleats",
    "工装口袋": "Cargo pockets",
    "开衩": "Slits",
    "开门襟设计": "Opening placket",
    "弧形下摆": "Curved hem",
    "弧形波浪": "Scalloped detail",
    "弧形腰线": "Curved waistline",
    "打揽": "Smocking",
    "扣袢": "Button loops",
    "扭结": "Twist detail",
    "抽绳": "Drawstring",
    "拉链": "Zip fastening",
    "拼接": "Panelled construction",
    "拼色": "Colour blocking",
    "插袋": "Inset pockets",
    "撞色纽扣": "Contrast buttons",
    "撞色芽条": "Contrast piping",
    "收褶": "Gathering",
    "斜插袋": "Slant pockets",
    "斜门襟": "Asymmetric placket",
    "无": "None",
    "暗门襟": "Concealed placket",
    "暗门襟/暗门襟设计": "Concealed placket",
    "暗门襟设计": "Concealed placket",
    "流苏": "Tassels",
    "皱褶": "Gathered detail",
    "盖袋": "Flap pockets",
    "系带": "Tie detail",
    "系带(交叉穿孔)": "Lace-up ties",
    "绑带": "Ties",
    "缝线细节": "Stitching details",
    "缩褶": "Gathering",
    "翻折袖": "Turn-up sleeves",
    "肩线前移": "Forward shoulder seams",
    "胸前口袋": "Chest pockets",
    "腰部绑带": "Waist ties",
    "自然卷边": "Rolled edges",
    "花边装饰": "Decorative trim",
    "芽条": "Piping",
    "荷叶边": "Ruffles",
    "蕾丝": "Lace",
    "蝴蝶结": "Bow detail",
    "袋盖": "Pocket flaps",
    "袋盖口袋": "Flap pockets",
    "袖中开衩": "Sleeve slits",
    "袖口开衩": "Cuff slits",
    "袖口纽扣": "Cuff buttons",
    "袖口缩褶": "Gathered cuffs",
    "袖袢": "Sleeve tabs",
    "装饰纽扣": "Decorative buttons",
    "装饰钮扣": "Decorative buttons",
    "调节纽扣": "Adjustable buttons",
    "贴袋": "Patch pockets",
    "连帽": "Hood",
    "连肩袖": "Cut-on sleeves",
    "镂空": "Openwork",
    "隐形口袋": "Concealed pockets",
    "隐形钩扣": "Concealed hook fastening",
    "青果领": "Shawl collar",
    "风琴片": "Accordion panels",
    "风琴腰节": "Accordion waist detail",
    "风琴袋": "Bellows pockets",
    "风琴褶": "Accordion pleats",
    "不含流梳": "Tassels not included",
    "含吊带": "Camisole included",
    "不含花边": "Decorative trim not included",
    "OT21/OT33同版型，共享尺码数据": "OT21/OT33 share the same fit and size measurements.",
    "SKT14/SKT21同版型，共享尺码数据": "SKT14/SKT21 share the same fit and size measurements."
};

  function translateGuideValue(value) {
    const text = String(value == null ? '' : value).trim();
    if (Object.prototype.hasOwnProperty.call(FIT_GUIDE_EN, text)) return FIT_GUIDE_EN[text];
    if (!/[\u3400-\u9fff]/.test(text)) return text;
    const translated = text.split(/[,，]/).map((part) => {
      const term = part.trim();
      return FIT_GUIDE_EN[term] || term;
    }).join(', ');
    // Never silently invent a translation for new source vocabulary.
    return /[\u3400-\u9fff]/.test(translated) ? 'Please contact us for details.' : translated;
  }

  const UNIT_SUFFIX = { cm: '(cm)', in: '(in)' };

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */

  function norm(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function safeParseJSON(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function round1(value) {
    return Math.round(value * 10) / 10;
  }

  function parseSkus(raw) {
    const parsed = safeParseJSON(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((sku) => sku != null && String(sku).trim() !== '')
      .map((sku) => String(sku));
  }

  /* Variant titles can be compound ("S / Blue"); a row matches when the size
   * label equals the full title or any of its slash-separated parts. */
  function labelMatchesSize(label, selected) {
    const normalizedLabel = norm(label);
    if (!normalizedLabel || selected == null) return false;
    if (normalizedLabel === norm(selected)) return true;
    return String(selected)
      .split('/')
      .some((part) => norm(part) === normalizedLabel);
  }

  function translateFitType(value) {
    const trimmed = String(value == null ? '' : value).trim();
    return FIT_TYPE_EN[trimmed] || trimmed;
  }

  function readSelectorValue(selector) {
    if (!selector) return '';
    if (selector.dataset.selectorType === 'listed') {
      const checked = selector.querySelector('.js-option:checked');
      return checked ? checked.value : '';
    }
    const customSelect = selector.querySelector('custom-select');
    if (customSelect) {
      const native = customSelect.querySelector('select');
      if (native) return native.value;
      const selectedOption = customSelect.querySelector('.custom-select__option[aria-selected="true"]');
      if (selectedOption) return selectedOption.dataset.value || selectedOption.textContent.trim();
      const buttonLabel = customSelect.querySelector('.custom-select__btn span');
      return buttonLabel ? buttonLabel.textContent.trim() : '';
    }
    const nativeSelect = selector.querySelector('select');
    return nativeSelect ? nativeSelect.value : '';
  }

  /* ------------------------------------------------------------------ *
   * Per-modal controller
   * ------------------------------------------------------------------ */

  function initSizeChart(modal) {
    if (!modal || modal.sizeChartInit) return;
    modal.sizeChartInit = true;

    const renderPoint = modal.querySelector('[data-size-chart-root]');
    const statusBox = modal.querySelector('[data-size-chart-status]');
    if (!renderPoint || !statusBox) return;

    const labels = Object.assign({}, FALLBACK_LABELS, safeParseJSON(modal.dataset.sizeChartLabels) || {});
    const gallery = (safeParseJSON(modal.dataset.sizeChartProductGallery || '') || [])
      .filter((item) => item && typeof item.src === 'string' && item.src.trim());

    const state = {
      unit: modal.dataset.sizeChartDefaultUnit === 'in' ? 'in' : 'cm',
      data: null,
      sizeLabels: null,
      request: null,
      table: null,
      unitButtons: null,
      controller: null,
      requestVariant: null,
      sequence: 0
    };

    /* ---------------- status helpers ---------------- */

    const unitControl = modal.querySelector('.size-chart-modal__unit');
    function showUnitControl(visible) {
      if (unitControl) unitControl.style.display = visible ? '' : 'none';
    }

    function showLoading() {
      showUnitControl(false);
      renderPoint.replaceChildren();
      statusBox.hidden = false;
      const box = el('div', 'szc-status szc-status--loading');
      const spinner = el('div', 'szc-status__spinner');
      spinner.setAttribute('aria-hidden', 'true');
      box.append(spinner, el('p', 'szc-status__text', LOADING_TEXT));
      statusBox.replaceChildren(box);
    }

    function hideStatus() {
      showUnitControl(true);
      statusBox.replaceChildren();
      statusBox.hidden = true;
    }

    function showUnavailable(kind) {
      showUnitControl(false);
      renderPoint.replaceChildren();
      statusBox.hidden = false;
      const box = el('div', 'szc-status szc-status--unavailable');
      box.append(el('p', 'szc-status__text', kind === 'unavailable' ? labels.unavailable_contact_message : labels.error_loading));
      const actions = el('div', 'szc-status__actions');
      const contact = el('a', 'szc-status__cta', labels.sizing_help_contact);
      contact.href = modal.dataset.sizeChartContactUrl || '/pages/contact';
      actions.append(contact);
      // A missing chart needs data review, not repeated requests or a guessed recommendation.
      if (kind !== 'unavailable') {
        const retry = el('button', 'szc-status__retry', labels.retry);
        retry.type = 'button';
        retry.addEventListener('click', () => load());
        actions.append(retry);
      }
      box.append(actions);
      statusBox.replaceChildren(box);
    }

    /* ---------------- fetch (in-flight requests deduplicated) ---------------- */

    function validChart(data) {
      if (!data || !Array.isArray(data.dimensions) || !data.dimensions.length ||
          !Array.isArray(data.rows) || !data.rows.length) return false;
      const keys = data.dimensions.map((dimension) => dimension && dimension.key);
      if (!keys.every((key) => typeof key === 'string' && key.trim()) || new Set(keys).size !== keys.length) return false;
      return data.rows.every((row) => {
        if (!row || typeof row.size_label !== 'string' || !row.size_label.trim() ||
            !row.measurements || typeof row.measurements !== 'object' || Array.isArray(row.measurements)) return false;
        const values = Object.entries(row.measurements);
        return values.length > 0 && values.every(([key, value]) =>
          keys.includes(key) && Number.isFinite(value) && value > 0 && value <= 500);
      });
    }

    function selectedVariantId() {
      const form = document.getElementById(modal.dataset.sizeChartFormId || '');
      const input = form && form.querySelector('[name="id"]');
      return String(input ? input.value : (modal.dataset.sizeChartInitialVariant || '')).trim();
    }

    function fetchChart(signal, variantId) {
      const base = String(modal.dataset.sizeChartApiBase || '/apps/smart-size').replace(/\/+$/, '');
      const params = new URLSearchParams();
      const gid = String(modal.dataset.sizeChartProductGid || '').trim();
      const handle = String(modal.dataset.sizeChartHandle || '').trim();
      const skus = parseSkus(modal.dataset.sizeChartSkus);
      if (gid) params.set('commerce_product_gid', gid);
      if (/^[0-9]+$/.test(variantId)) params.set('commerce_variant_gid', `gid://shopify/ProductVariant/${variantId}`);
      if (handle) params.set('handle', handle);
      if (skus.length) params.set('skus', JSON.stringify(skus));

      // Liquid emits this marker only for an explicitly enabled unpublished draft.
      const preview = modal.dataset.sizeChartPreview;
      if (preview === 'rc20261015') params.set('size_chart_preview', preview);

      return fetch(`${base}/size-chart?${params.toString()}`, {
        method: 'GET',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal
      }).then((response) => {
        if (response.status === 404) {
          /* Contract §1.3: mapped-but-empty / unmapped product → guidance state. */
          return response
            .json()
            .catch(() => ({}))
            .then((body) => {
              const error = new Error((body && body.code) || 'SIZE_CHART_MISSING');
              error.reason = 'unavailable';
              throw error;
            });
        }
        if (!response.ok) {
          const error = new Error(`Size chart request failed with HTTP ${response.status}`);
          error.reason = 'error';
          throw error;
        }
        return response.json().catch(() => {
          const error = new Error('Size chart response is not valid JSON');
          error.reason = 'error';
          throw error;
        });
      });
    }

    function load() {
      if (modal.dataset.sizeChartFallbackError) return;
      const variantId = selectedVariantId();
      if (state.request && state.requestVariant === variantId) return;
      if (state.controller) state.controller.abort();
      const sequence = ++state.sequence;
      state.requestVariant = variantId;
      state.data = null;
      state.sizeLabels = null;
      state.table = null;
      showLoading();
      const controller = new AbortController();
      state.controller = controller;
      const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      state.request = fetchChart(controller.signal, variantId)
        .then((data) => {
          if (sequence !== state.sequence) return;
          if (!validChart(data)) throw new Error('Size chart response has invalid measurements');
          state.data = data;
          collectSizeLabels();
          document.querySelectorAll('variant-picker').forEach((picker) => {
            if (!picker.productForm || picker.productForm.getAttribute('id') !== modal.dataset.sizeChartFormId) return;
            modal.dataset.sizeChartSelectedSize = '';
            for (const selector of picker.querySelectorAll('.option-selector[data-option]')) {
              const value = readSelectorValue(selector);
              if (value && state.sizeLabels.has(norm(value))) {
                modal.dataset.sizeChartSelectedSize = value;
                break;
              }
            }
          });
          renderData();
          hideStatus();
        })
        .catch((error) => {
          if (sequence !== state.sequence) return;
          state.data = null;
          state.sizeLabels = null;
          state.table = null;
          showUnavailable(error && error.reason === 'unavailable' ? 'unavailable' : 'error');
          if (modal.dataset.sizeChartSwitchUrl && modal.hasAttribute('open')) modal.dispatchEvent(new CustomEvent('size-chart:failed'));
        })
        .then(() => {
          window.clearTimeout(timer);
          if (sequence !== state.sequence) return;
          state.request = null;
          state.controller = null;
        });
    }

    /* Revalidate on every open so a newly quarantined chart cannot stay cached. */
    if (modal.hasAttribute('open')) {
      load();
    }
    const openObserver = new MutationObserver(() => {
      if (modal.hasAttribute('open')) {
        load();
      } else {
        ++state.sequence;
        if (state.controller) state.controller.abort();
        state.request = null;
        state.controller = null;
      }
    });
    openObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });

    /* ---------------- data helpers ---------------- */

    function chartRows() {
      return state.data && Array.isArray(state.data.rows) ? state.data.rows : [];
    }

    function chartDimensions() {
      return state.data && Array.isArray(state.data.dimensions) ? state.data.dimensions : [];
    }

    function collectSizeLabels() {
      state.sizeLabels = new Set(
        chartRows()
          .map((row) => norm(row && row.size_label))
          .filter(Boolean)
      );
    }

    /* ---------------- unit toggle (topbar) ---------------- */

    function syncUnitButtons() {
      if (!state.unitButtons) return;
      [['cm', state.unitButtons.cm], ['in', state.unitButtons.in]].forEach(([unit, button]) => {
        if (!button) return;
        const active = state.unit === unit;
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('is-active', active);
      });
    }

    function bindUnitButtons() {
      const cmButton = modal.querySelector('[data-size-chart-unit-cm]');
      const inButton = modal.querySelector('[data-size-chart-unit-in]');
      state.unitButtons = { cm: cmButton, in: inButton };
      [cmButton, inButton].forEach((button) => {
        if (!button) return;
        button.classList.add('szc-unit-btn');
        button.addEventListener('click', () => {
          const unit = button === inButton ? 'in' : 'cm';
          if (state.unit === unit) return;
          state.unit = unit;
          syncUnitButtons();
          renderTable();
          renderModelsBand();
        });
      });
      syncUnitButtons();
    }

    /* ---------------- table ---------------- */

    function measurementText(value) {
      if (value == null || value === '') return '';
      if (typeof value === 'string' && value.trim() === '') return '';
      const num = typeof value === 'number' ? value : Number(value);
      if (!isFinite(num)) return String(value);
      return String(state.unit === 'in' ? round1(num / CM_PER_IN) : round1(num));
    }

    function dimensionTitle(dim) {
      const code = dim && dim.code ? `${dim.code} ` : '';
      const name = dim && (dim.label || dim.key) ? String(dim.label || dim.key) : '';
      return `${code}${name} ${UNIT_SUFFIX[state.unit]}`;
    }

    function modelMeasurementText(value) {
      if (value == null || value === '') return '—';
      const raw = String(value).trim();
      if (!raw) return '—';
      const num = Number(value);
      if (!isFinite(num)) return raw;
      const displayed = state.unit === 'in' ? round1(num / CM_PER_IN) : round1(num);
      return `${displayed}${state.unit}`;
    }

    function renderTable() {
      const table = state.table;
      if (!table || !state.data) return;
      const thead = document.createElement('thead');
      const headRow = document.createElement('tr');
      const sizeHead = el('th', null, labels.table_header_size);
      sizeHead.setAttribute('scope', 'col');
      headRow.append(sizeHead);
      chartDimensions().forEach((dim) => {
        const th = el('th', null, dimensionTitle(dim));
        th.setAttribute('scope', 'col');
        headRow.append(th);
      });
      thead.append(headRow);

      const tbody = document.createElement('tbody');
      chartRows().forEach((row) => {
        const tr = document.createElement('tr');
        const sizeLabel = row && row.size_label != null ? String(row.size_label) : '';
        tr.dataset.sizeLabel = sizeLabel;
        const rowHead = el('th', null, sizeLabel);
        rowHead.setAttribute('scope', 'row');
        tr.append(rowHead);
        const measurements = (row && row.measurements) || {};
        chartDimensions().forEach((dim) => {
          tr.append(el('td', null, measurementText(measurements ? measurements[dim.key] : null)));
        });
        tbody.append(tr);
      });

      table.replaceChildren(thead, tbody);
      refreshHighlight();
    }

    function refreshHighlight() {
      const table = state.table;
      if (!table) return;
      const selected = modal.dataset.sizeChartSelectedSize;
      table.querySelectorAll('tbody tr').forEach((tr) => {
        tr.classList.toggle('tr--highlight', labelMatchesSize(tr.dataset.sizeLabel, selected));
      });
    }

    /* ---------------- render blocks (contract §2.3 order) ---------------- */

    function buildModelsBand() {
      /* The backend's manifest-verified model table is the only trusted source.
       * When it is unavailable, keep the model column visible with the product's
       * real campaign image and explicit dashes; never use product-embedded or
       * theme-editor model data as an unversioned fallback. */
      const apiProfiles = Array.isArray(state.data && state.data.model_reference)
        ? state.data.model_reference.map((model) => ({
          ...model,
          photo: model && model.photo_url
        }))
        : null;
      let list = apiProfiles && apiProfiles.length ? apiProfiles : null;
      if (!Array.isArray(list) || !list.length) {
        /* Keep the section visible for the Size Chart contract. The image is
         * labelled as a product-model reference; no name, size, or body value
         * is inferred from the photo. */
        list = [{
          name: labels.product_model || 'Product model',
          photo: gallery[0] && gallery[0].src,
          placeholder: true
        }];
      }

      const band = el('div', 'szc-models');
      const head = el('div', 'szc-models__head');
      head.append(el('b', 'szc-models__title', labels.model_ref_title));
      if (labels.model_ref_subtitle) {
        head.append(el('span', 'szc-models__subtitle', labels.model_ref_subtitle));
      }
      band.append(head);

      const grid = el('div', 'szc-models__grid');
      list.forEach((model) => {
        if (!model || typeof model !== 'object') return;
        const name = String(model.name || '').trim();
        const tryonSize = String(model.try_on_size || model.size || '').trim();

        const cell = el('div', 'szc-model');
        if (name) cell.append(el('div', 'szc-model__name', name));

        const photo = el('div', 'szc-model__photo');
        const photoUrl = String(model.photo || model.photo_url || '').trim();
        if (photoUrl) {
          const img = document.createElement('img');
          img.src = photoUrl;
          img.alt = '';
          img.loading = 'lazy';
          /* No .szc-model__photo img rule exists in T4 CSS; inline sizing keeps
           * the photo inside the placeholder frame without touching CSS. */
          img.setAttribute('style', 'width:100%;height:100%;object-fit:cover;');
          photo.append(img);
        } else if (name) {
          photo.textContent = name.charAt(0).toUpperCase();
        }
        if (tryonSize) photo.setAttribute('data-size', tryonSize);
        cell.append(photo);

        const hips = model.hip_cm != null ? model.hip_cm : model.hips_cm;
        [
          [labels.model_height, model.height_cm],
          [labels.model_bust, model.bust_cm],
          [labels.model_waist, model.waist_cm],
          [labels.model_hips, hips]
        ].forEach(([label, value]) => {
          const item = el('div', 'szc-model__cell');
          item.append(el('span', null, label), el('b', null, modelMeasurementText(value)));
          cell.append(item);
        });

        const tryon = el('div', 'szc-model__cell');
        tryon.append(el('span', null, labels.model_tryon_size), el('b', null, tryonSize || '—'));
        cell.append(tryon);
        grid.append(cell);
      });

      if (!grid.children.length) return null;
      band.append(grid);
      if (list.some((model) => model && model.placeholder)) {
        band.append(el('p', 'szc-models__notice', labels.model_ref_notice || 'Body measurements are not available for this item yet.'));
      }
      return band;
    }

    function renderModelsBand() {
      if (!state.data) return;
      const current = renderPoint.querySelector('.szc-models');
      const next = buildModelsBand();
      if (current && next) {
        current.replaceWith(next);
      } else if (!current && next) {
        renderPoint.prepend(next);
      } else if (current && !next) {
        current.remove();
      }
    }

    function buildFitGuide() {
      const guide = el('div', 'szc-fit-guide');
      guide.append(el('b', 'szc-fit-guide__title', labels.fit_guide_tab));

      const addRow = (labelText, valueText) => {
        const value = translateGuideValue(valueText);
        if (!value || !labelText) return;
        const item = el('div', 'szc-fit-guide__item');
        item.append(el('span', 'szc-fit-guide__label', labelText));
        item.append(el('span', 'szc-fit-guide__value', value));
        guide.append(item);
      };

      const data = state.data;
      addRow(labels.fit_type_label, translateFitType(data.fit_type));
      addRow(labels.material_label, data.material);
      addRow(labels.stretch_label, data.stretch);

      /* design_details → chip list (comma separated, blanks skipped). */
      const chips = String(data.design_details || '')
        .split(/[,，]/)
        .map((chip) => translateGuideValue(chip))
        .filter(Boolean);
      if (chips.length && labels.design_label) {
        const item = el('div', 'szc-fit-guide__item');
        item.append(el('span', 'szc-fit-guide__label', labels.design_label));
        chips.forEach((chip) => item.append(el('span', 'szc-fit-guide__value', chip)));
        guide.append(item);
      }

      addRow(labels.notes_label, data.other_notes);

      return guide;
    }

    function buildMap() {
      const category = norm(state.data.category);
      const sources = { tops: 'sizeChartMapTops', bottoms: 'sizeChartMapBottoms', dresses: 'sizeChartMapDresses' };
      const field = Object.prototype.hasOwnProperty.call(sources, category) ? sources[category] : null;
      const configuredUrl = field ? String(modal.dataset[field] || '').trim() : '';
      const fallbackUrl = String(modal.dataset.sizeChartMapDefault || '').trim();
      const url = String(modal.dataset.sizeChartMapDesktop || fallbackUrl || configuredUrl).trim();
      const mobileUrl = String(modal.dataset.sizeChartMapMobile || url).trim();
      const map = el('div', 'szc-map');
      map.append(el('h3', 'szc-map__title', labels.how_to_measure_title));
      const content = el('div', 'szc-map__content');
      const steps = el('ol', 'szc-map__steps');
      ['shoulder', 'bust', 'waist', 'hips'].forEach((part) => {
        const step = el('li');
        step.append(el('b', null, labels[`measure_${part}_label`]), el('p', null, labels[`measure_${part}_copy`]));
        steps.append(step);
      });
      const image = el('div', 'szc-map__image');
      if (url) {
        const picture = document.createElement('picture');
        const mobileSource = document.createElement('source');
        mobileSource.media = '(max-width: 760px)';
        mobileSource.srcset = mobileUrl;
        const img = document.createElement('img');
        img.src = url;
        img.alt = labels.map_title;
        img.loading = 'lazy';
        img.addEventListener('error', () => {
          image.replaceChildren();
          image.classList.add('szc-map__image--placeholder');
          image.append(el('span', 'szc-map__placeholder-label', labels.map_title));
          image.append(el('span', 'szc-map__placeholder-copy', 'Use the highlighted points to compare each measurement.'));
        }, { once: true });
        picture.append(mobileSource, img);
        const zoom = el('button', 'szc-map__zoom');
        zoom.type = 'button';
        zoom.setAttribute('aria-label', labels.map_title);
        zoom.addEventListener('click', () => openImageLightbox(img.currentSrc || url, labels.map_title, true));
        image.append(picture, zoom);
      } else {
        image.classList.add('szc-map__image--placeholder');
        image.append(el('span', 'szc-map__placeholder-label', labels.map_title));
        image.append(el('span', 'szc-map__placeholder-copy', 'Use the highlighted points to compare each measurement.'));
      }
      content.append(steps, image);
      map.append(content);
      return map;
    }

    function currentProductReviews() {
      const widget = document.getElementById('judgeme_product_reviews');
      const productId = String(modal.dataset.sizeChartProductGid || '').split('/').pop();
      const widgetProductId = widget && (widget.dataset.id || widget.dataset.productId);
      // A global review carousel or a different product's widget is not evidence.
      return widget && productId && String(widgetProductId) === productId ? widget : null;
    }

    function reviewSize(review) {
      // A size must be explicitly supplied, never estimated from body measurements.
      const atom = '(?:extra[ -]*(?:extra[ -]*)?(?:small|large)|[2-5]XL|XXXS|XXS|XXXL|XXL|XL|XS|S|M|L|small|medium|large|one[ -]*size|OS|(?:UK|US|EU)\\s*\\d{1,2}|\\d{1,2})';
      const sizePattern = atom + '(?:\\s*[/–-]\\s*' + atom + ')?';
      const normaliseSize = (raw) => {
        const text = raw.trim().toLowerCase().replace(/extra[ -]*extra[ -]*small/g, 'xxs')
          .replace(/extra[ -]*extra[ -]*large/g, 'xxl').replace(/extra[ -]*small/g, 'xs')
          .replace(/extra[ -]*large/g, 'xl').replace(/\bsmall\b/g, 's')
          .replace(/\bmedium\b/g, 'm').replace(/\blarge\b/g, 'l')
          .replace(/\b2xl\b/g, 'xxl').replace(/\b3xl\b/g, 'xxxl')
          .replace(/one[ -]*size|\bos\b/g, 'One Size')
          .replace(/\b(uk|us|eu)\s*(\d+)/g, '$1 $2').replace(/\s*([/–-])\s*/g, '$1');
        return text === 'One Size' ? text : text.toUpperCase();
      };
      const candidates = new Set();
      let ambiguous = false;
      const add = (value) => candidates.add(normaliseSize(value));
      const exact = new RegExp('^(?:size\\s*[:：]?\\s*)?(' + sizePattern + ')$', 'i');
      const invalidContext = /\b(usually|usual|normally|typically|between|or|if|would|could|should|wish|not|never|returned|returning|exchanged|daughter|friend|wife|husband|sister|mother)\b/i;
      const explicitSizes = (text) => {
        // Keep clauses independent: "usually M, but ordered L" supplies L.
        const clauses = text.replace(/[’‘]/g, "'").split(/[.!?;\n]|,|\bbut\b/i);
        const patterns = [
          new RegExp('\\b(?:I\\s+)?(?:bought|purchased|ordered|got|chose|selected|wearing|am wearing|I\\\'m wearing)\\s+(?:(?:this|it|the (?:shirt|top|dress|pants|trousers))\\s+)?(?:in\\s+)?(?:an?\\s+|the\\s+)?(?:size\\s*[:：]?\\s*)?(' + sizePattern + ')(?![\\w])', 'gi'),
          new RegExp('\\b(?:size (?:purchased|ordered|worn)|purchased size|ordered size|purchased|ordered|wears)\\s*[:：-]?\\s*(' + sizePattern + ')(?![\\w])', 'gi'),
          new RegExp('\\b(?:the|my)\\s+(?:size\\s+)?(' + sizePattern + ')\\s+fits\\s+(?:me\\b|perfectly\\b|well\\b|beautifully\\b|great\\b)', 'gi'),
          new RegExp('\\bsize\\s*[:：]\\s*(' + sizePattern + ')(?![\\w])', 'gi'),
          new RegExp('\\bI\\s+sized\\s+(?:up|down)\\s+to\\s+(?:an?\\s+)?(?:size\\s*[:：]?\\s*)?(' + sizePattern + ')(?![\\w])', 'gi')
        ];
        let found = false;
        // Return/exchange stories do not establish which size appears in the photo.
        if (/\b(returned|returning|exchanged)\b/i.test(text)) { ambiguous = true; return false; }
        for (const clause of clauses) {
          if (invalidContext.test(clause)) continue;
          for (const pattern of patterns) {
            for (const match of clause.matchAll(pattern)) {
              const prefix = clause.slice(0, match.index);
              // An inline 'Size: XL' is evidence; 'bust size: 36' is a body measurement.
              if (/\b(?:bust|waist|hips?|shoulder|shoe)\s*$/i.test(prefix)) continue;
              const tail = clause.slice(match.index + match[0].length);
              if (/^\s*(?:cm|kg|lbs?|inches|shirts?|tops?|dresses|items?)\b/i.test(tail)) continue;
              if (new RegExp('^\\s*(?:and|&)\\s+(?:an?\\s+)?(?:size\\s+)?' + atom + '\\b', 'i').test(tail)) { ambiguous = true; continue; }
              add(match[1]); found = true;
            }
          }
        }
        return found;
      };
      const fields = review.querySelectorAll('.jdgm-rev__cf-ans, .jdgm-rev__custom-field');
      for (const field of fields) {
        const title = field.querySelector('.jdgm-rev__cf-ans__title, .jdgm-rev__custom-field-title');
        const value = field.querySelector('.jdgm-rev__cf-ans__value, .jdgm-rev__custom-field-value');
        const label = title && title.textContent.trim().replace(/[:：\s]+$/, '').trim();
        const supplied = value && value.textContent.trim();
        if (!label || !supplied) continue;
        if (/^(size(?: purchased| ordered| worn)?|purchased size|ordered size|wears?)$/i.test(label)) {
          const match = supplied.match(exact);
          if (match) add(match[1]);
          else if (!explicitSizes(supplied)) ambiguous = true;
        } else if (/^sizing info$/i.test(label)) {
          if (explicitSizes(supplied)) continue;
          if (invalidContext.test(supplied) || /\b(now|was|previous|bust)\b/i.test(supplied)) continue;
          const exactMatch = supplied.match(exact);
          if (exactMatch) { add(exactMatch[1]); continue; }
          // Numeric sizes require a size/region label; bare numbers can be height/weight.
          const marked = new RegExp('\\b(?:size\\s*[:：]?\\s*|(?=(?:UK|US|EU)\\s*\\d))(' + sizePattern + ')(?![\\w])', 'gi');
          const matches = [...supplied.matchAll(marked)];
          if (matches.length) matches.forEach((match) => add(match[1]));
          else {
            const letters = supplied.match(/\b(?:[2-5]XL|XXXS|XXS|XXXL|XXL|XL|XS|S|M|L|small|medium|large)\b/gi) || [];
            if (letters.length === 1) add(letters[0]);
            else if (letters.length > 1) ambiguous = true;
          }
        }
      }
      const prose = Array.from(review.querySelectorAll('.jdgm-rev__title, .jdgm-rev__body')).map((node) => node.textContent).join('\n');
      explicitSizes(prose);
      return !ambiguous && candidates.size === 1 ? Array.from(candidates)[0] : '';
    }

    let reviewPhotoDocument = null;
    let reviewPhotosRequest = null;

    function loadReviewPhotos(onLoaded) {
      if (reviewPhotosRequest || reviewPhotoDocument || !currentProductReviews()) return;
      const shop = String(modal.dataset.sizeChartStorefront || '').trim();
      const productId = String(modal.dataset.sizeChartProductGid || '').split('/').pop();
      if (!/^[a-z0-9-]+\.myshopify\.com$/i.test(shop) || !/^\d+$/.test(productId)) return;
      // Same public, read-only endpoint used by this product's Judge.me widget.
      // Fetch picture reviews independently so we never change the PDP's sort order.
      const query = new URLSearchParams({ url: shop, shop_domain: shop, platform: 'shopify',
        per_page: '5', product_id: productId, sort_by: 'with_pictures' });
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      reviewPhotosRequest = fetch(`https://cdn.judge.me/reviews/reviews_for_widget?${query}`, {
        signal: controller.signal, credentials: 'omit'
      }).then((response) => {
        if (!response.ok) throw new Error('Review photos unavailable');
        return response.json();
      }).then((data) => {
        if (!data || typeof data.html !== 'string') throw new Error('Invalid review photos');
        // Parse inertly, read only image URLs/size text; never insert provider HTML.
        reviewPhotoDocument = new DOMParser().parseFromString(data.html, 'text/html');
        if (modal.isConnected) onLoaded();
      }).catch(() => {
        // Existing in-page photos remain available if the provider is blocked.
      }).finally(() => {
        window.clearTimeout(timer);
        reviewPhotosRequest = null;
      });
    }

    function buildCustomerFitPhotos() {
      const section = el('section', 'szc-customer-fit');
      section.append(el('b', 'szc-customer-fit__title', labels.customer_fit_photos));
      const stage = el('div', 'szc-customer-fit__stage');
      stage.setAttribute('aria-label', labels.customer_fit_photos);
      const widget = currentProductReviews();
      const photos = [];
      const seen = new Set();
      if (widget) (reviewPhotoDocument || widget).querySelectorAll('.jdgm-rev').forEach((review) => {
        const size = reviewSize(review);
        review.querySelectorAll('.jdgm-rev__pic-img, .jdgm-rev__pic img').forEach((image) => {
          const link = image.closest('.jdgm-rev__pic-link');
          const raw = (link && link.getAttribute('href')) || image.getAttribute('data-src') || image.getAttribute('data-lazy-src') ||
            image.getAttribute('data-original') || image.currentSrc || image.getAttribute('src');
          if (!raw) return;
          let url;
          try { url = new URL(raw, document.baseURI); } catch (error) { return; }
          if (!['https:', 'http:'].includes(url.protocol) || seen.has(url.href)) return;
          seen.add(url.href);
          photos.push({ src: url.href, alt: image.alt || labels.customer_fit_photo, size });
        });
      });
      section.dataset.reviewSignature = JSON.stringify(photos);
      photos.slice(0, 3).forEach((item) => {
        const card = el('article', 'szc-customer-fit__card');
        const image = document.createElement('img');
        image.className = 'szc-customer-fit__thumb';
        image.src = item.src;
        image.alt = item.alt;
        image.loading = 'lazy';
        const mediaButton = el('button', 'szc-customer-fit__media');
        mediaButton.type = 'button';
        mediaButton.setAttribute('aria-label', item.alt);
        mediaButton.append(image);
        mediaButton.addEventListener('click', () => openImageLightbox(item.src, item.alt));
        card.append(mediaButton, el('span', 'szc-customer-fit__size', item.size ? `${labels.customer_wears} ${item.size}` : ''));
        stage.append(card);
      });
      if (photos.length) section.append(stage);
      else section.append(el('p', 'szc-customer-fit__empty', labels.customer_fit_empty));
      return section;
    }

    function buildSizingHelp() {
      const section = el('section', 'szc-sizing-help');
      section.append(el('h3', 'szc-sizing-help__title', labels.sizing_help_title));
      const link = el('a', 'szc-sizing-help__link', labels.sizing_help_contact);
      link.href = modal.dataset.sizeChartContactUrl || '/pages/contact';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      section.append(link);
      return section;
    }

    function openImageLightbox(src, alt, zoomable = false) {
      if (!src) return;
      const existing = modal.querySelector('.szc-customer-fit__lightbox');
      if (existing) existing.remove();

      const lightbox = el('div', 'szc-customer-fit__lightbox');
      if (zoomable) lightbox.classList.add('szc-map__lightbox');
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-label', alt || labels.customer_fit_photo || 'Customer photo');
      const frame = el('div', 'szc-customer-fit__lightbox-frame');
      const close = el('button', 'szc-customer-fit__lightbox-close', '×');
      close.type = 'button';
      close.setAttribute('aria-label', modal.querySelector('.modal__close-btn .visually-hidden')?.textContent?.trim() || 'Close image');
      const image = document.createElement('img');
      image.src = src;
      image.alt = alt;
      image.loading = 'eager';

      if (zoomable) {
        image.tabIndex = 0;
        image.setAttribute('role', 'button');
        image.setAttribute('aria-label', alt);
        image.setAttribute('aria-pressed', 'false');
        const toggleZoom = () => {
          const enlarged = frame.classList.toggle('is-zoomed');
          image.setAttribute('aria-pressed', String(enlarged));
          frame.scrollLeft = enlarged ? (frame.scrollWidth - frame.clientWidth) / 2 : 0;
          frame.scrollTop = enlarged ? (frame.scrollHeight - frame.clientHeight) / 2 : 0;
        };
        image.addEventListener('click', toggleZoom);
        image.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleZoom();
          }
        });
      }

      let previousFocus = document.activeElement;
      const closeLightbox = () => {
        document.removeEventListener('keydown', onKeydown);
        lightbox.remove();
        if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
      };
      const onKeydown = (event) => {
        if (event.key === 'Escape') closeLightbox();
      };
      close.addEventListener('click', closeLightbox);
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) closeLightbox();
      });
      frame.append(close, image);
      lightbox.append(frame);
      modal.append(lightbox);
      if (zoomable && window.matchMedia('(max-width: 760px)').matches) {
        frame.classList.add('is-zoomed');
        image.setAttribute('aria-pressed', 'true');
        const centerZoom = () => requestAnimationFrame(() => {
          frame.scrollLeft = (frame.scrollWidth - frame.clientWidth) / 2;
          frame.scrollTop = (frame.scrollHeight - frame.clientHeight) / 2;
        });
        image.addEventListener('load', centerZoom, { once: true });
        centerZoom();
      }
      document.addEventListener('keydown', onKeydown);
      close.focus();
    }

    let reviewObserver = null;
    let reviewRefreshTimer = null;

    function refreshCustomerFitPhotos() {
      if (reviewObserver) reviewObserver.disconnect();
      window.clearTimeout(reviewRefreshTimer);
      let widget = currentProductReviews();
      const refresh = () => {
        const panel = renderPoint.querySelector('.szc-table-panel');
        const current = panel && panel.querySelector('.szc-customer-fit');
        if (!current) return;
        const next = buildCustomerFitPhotos();
        if (next.dataset.reviewSignature !== current.dataset.reviewSignature) current.replaceWith(next);
      };
      // Watch for late widget insertion and lazy images, rather than an 8s cutoff.
      // Ignore our own modal rendering to avoid observer feedback loops.
      reviewObserver = new MutationObserver((records) => {
        if (!modal.isConnected) {
          reviewObserver.disconnect();
          window.clearTimeout(reviewRefreshTimer);
          return;
        }
        const nextWidget = currentProductReviews();
        const changed = nextWidget !== widget || records.some((record) =>
          nextWidget && (record.target === nextWidget || nextWidget.contains(record.target)));
        widget = nextWidget;
        if (!changed) return;
        window.clearTimeout(reviewRefreshTimer);
        reviewRefreshTimer = window.setTimeout(() => { refresh(); loadReviewPhotos(refresh); }, 50);
      });
      reviewObserver.observe(document.body, {
        childList: true, subtree: true, characterData: true, attributes: true,
        attributeFilter: ['src', 'srcset', 'data-src', 'data-lazy-src', 'data-original', 'data-id', 'data-product-id']
      });
      refresh();
      loadReviewPhotos(refresh);
    }

    function renderData() {
      const data = state.data;
      if (!data || typeof data !== 'object' || !chartDimensions().length || !chartRows().length) {
        showUnavailable('error');
        return;
      }

      renderPoint.replaceChildren();
      const fragment = document.createDocumentFragment();

      /* 1. Model reference (filtered by the backend to this product's SPU). */
      const models = buildModelsBand();
      if (models) fragment.append(models);

      const layout = el('div', 'szc-layout');
      const panel = el('div', 'szc-table-panel');
      const main = el('div', 'szc-main');

      /* 2. Tabs. */
      const showFitGuide = modal.dataset.sizeChartShowFitGuide === 'true';
      const tabs = el('div', 'szc-tabs');
      tabs.setAttribute('role', 'tablist');

      const tableView = el('div');
      tableView.setAttribute('role', 'tabpanel');
      const table = document.createElement('table');
      table.className = 'szc-table';
      state.table = table;
      const tableWrap = el('div', 'szc-table-wrap');
      tableWrap.tabIndex = 0;
      tableWrap.setAttribute('role', 'region');
      tableWrap.setAttribute('aria-label', labels.measurements_tab);
      tableWrap.append(table);
      tableView.append(tableWrap);

      const guideView = buildFitGuide();
      guideView.setAttribute('role', 'tabpanel');
      guideView.hidden = true;

      const tabDefs = [{ name: 'measurements', label: labels.measurements_tab, view: tableView }];
      if (showFitGuide) tabDefs.push({ name: 'guide', label: labels.fit_guide_tab, view: guideView });

      const selectTab = (name) => {
        tabDefs.forEach((def) => {
          const active = def.name === name;
          def.button.setAttribute('aria-selected', String(active));
          def.button.classList.toggle('szc-tab--active', active);
          def.view.hidden = !active;
        });
      };
      tabDefs.forEach((def) => {
        const button = el('button', 'szc-tab', def.label);
        button.type = 'button';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', 'false');
        button.addEventListener('click', () => selectTab(def.name));
        def.button = button;
        tabs.append(button);
      });

      /* 3/4. Table + fit guide views (tab-switched inside .szc-main). */
      main.append(tabs, tableView);
      if (showFitGuide) main.append(guideView);
      panel.append(main);
      const customerFitPhotos = buildCustomerFitPhotos();
      if (customerFitPhotos) panel.append(customerFitPhotos);
      panel.append(buildMap(), buildSizingHelp());
      layout.append(panel);

      fragment.append(layout);
      renderPoint.append(fragment);
      refreshCustomerFitPhotos();

      selectTab('measurements');
      renderTable();
    }

    /* ---------------- variant → highlight linking ---------------- */

    function onDocumentChange(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (target.name === 'id' && target.form && target.form.getAttribute('id') === modal.dataset.sizeChartFormId) {
        if (modal.hasAttribute('open')) load();
      }
      const picker = target.closest('variant-picker');
      if (picker && picker.productForm && picker.productForm.getAttribute('id') !== modal.dataset.sizeChartFormId) return;
      if (!picker || !state.sizeLabels || !state.sizeLabels.size) return;
      const selectors = picker.querySelectorAll('.option-selector[data-option]');
      modal.dataset.sizeChartSelectedSize = '';
      for (const selector of selectors) {
        const value = readSelectorValue(selector);
        if (value && state.sizeLabels.has(norm(value))) {
          modal.dataset.sizeChartSelectedSize = value;
          refreshHighlight();
          return;
        }
      }
      refreshHighlight();
    }

    document.addEventListener('change', onDocumentChange);
    document.addEventListener('on:variant:change', (event) => {
      if (!event.detail || !event.detail.form || event.detail.form.getAttribute('id') !== modal.dataset.sizeChartFormId) return;
      if (modal.hasAttribute('open')) load();
    });

    bindUnitButtons();
  }

  /* ------------------------------------------------------------------ *
   * Boot (defer-friendly)
   * ------------------------------------------------------------------ */

  function init() {
    document.querySelectorAll('modal-dialog[data-size-chart-root]').forEach(initSizeChart);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
